import React, { useState, useEffect } from 'react';
import './InvoiceTemplatePanel.css';
import { X, Search, Download, FileText } from 'lucide-react';
import Loader from '../../../components/Loader/Loader';
import Swal from 'sweetalert2';
import API_ENDPOINTS from '../../../config/apiEndpoints';
import html2pdf from 'html2pdf.js';

const InvoiceTemplatePanel = ({ isOpen, onClose, invoice, getToken, API_BASE, NEW_BASE_URL }) => {
    const [loading, setLoading] = useState(false);
    const [templates, setTemplates] = useState([{ templateId: '0', templateName: 'System Default Template' }]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState({ templateId: '0', templateName: 'System Default Template' });

    const GET_DOWNLOAD_TEMPLATE = `${NEW_BASE_URL}/Editor/ManageTemplate`;
    const WCF_DEV_URL = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
    const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
    const DOWNLOAD_INVOICE_TEMPLATE = `${NEWV3_BASE_URL}/${API_ENDPOINTS.INVOICE.DOWNLOAD_INVOICE_TEMPLATE}`.trim();
    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const parentId = localStorage.getItem('ParentID') || localStorage.getItem('userId') || '34594';
            const token = getToken() || "-2295521862261168";
            const payload = {
                Token: String(token),
                Details: {
                    operation: "GET",
                    templateId: null,
                    isActiveFilter: true,
                    parentId: String(parentId),
                    templateType: "invoice"
                }
            };
            const response = await fetch(GET_DOWNLOAD_TEMPLATE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const defaultOption = { templateId: '0', templateName: 'System Default Template' };

            if (response.ok) {
                const data = await response.json();
                if (data?.Details && data.Details.length > 0) {
                    const jsonResult = data.Details[0].jsonResult;
                    const list = typeof jsonResult === 'string' ? JSON.parse(jsonResult) : jsonResult;
                    setTemplates([defaultOption, ...(Array.isArray(list) ? list : [])]);
                } else {
                    setTemplates([defaultOption]);
                }
            } else {
                setTemplates([defaultOption]);
            }
        } catch (error) {
            console.error('Error fetching invoice templates:', error);
            const defaultOption = { templateId: '0', templateName: 'System Default Template' };
            setTemplates([defaultOption]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchTemplates();
        }
    }, [isOpen]);

    const handleDownload = async () => {
        if (!selectedTemplate) return;
        
        const isDefault = selectedTemplate.templateId === '0' || selectedTemplate.templateId === 0;
        const templateName = selectedTemplate.templateName || selectedTemplate.TemplateName;
        if (!isDefault && !templateName) return;

        const invoiceId = invoice?.InvoiceId || invoice?.ID || invoice?.Id;
        if (!invoiceId) {
            Swal.fire({ title: 'Error', text: 'Invoice ID not found', icon: 'error' });
            return;
        }

        setLoading(true);
        try {
            if (isDefault) {
                // Use the exact same form submission logic from the webforms project for the default template
                // The ASMX service is hosted on the main webforms domain
                const API_BASE_WEBFORMS = 'https://www.kit19.com'; 
                const form = document.createElement('form');
                form.method = 'POST';
                form.action = `${API_BASE_WEBFORMS}/UserCRMCampaign/Service/InvoiceService.asmx/DownloadInvoiceHtmltoPdf`;

                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = 'InvoiceId';
                input.value = invoiceId;

                form.appendChild(input);
                document.body.appendChild(form);
                form.submit();
                document.body.removeChild(form);
                
                onClose();
                setLoading(false);
                return;
            }

            const templateUrl = `https://docs.kit19.com/kit19/DocumentTemplates/${templateName}.html`;
            const payload = {
                Token: "-2295521862261168",
                Details: {
                    InvoiceId: parseInt(invoiceId, 10),
                    TemplateUrl: templateUrl,
                    IsPreview: false
                }
            };

            const response = await fetch(DOWNLOAD_INVOICE_TEMPLATE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error('Failed to fetch invoice HTML');

            const responseText = await response.text();
            let htmlString = responseText;
            
            try {
                const result = JSON.parse(responseText);
                htmlString = result?.Details || result?.d || result?.Data || result?.data || result || responseText;
            } catch(e) {
                // It's raw HTML text
            }

            if (!htmlString || typeof htmlString !== 'string') {
                throw new Error('Invalid HTML response received');
            }

            // Fix relative URLs to point to kit19.com so styles and images load correctly
            let fixedHtmlString = htmlString
                .replace(/href="\/(?!\/)/g, 'href="https://kit19.com/')
                .replace(/src="\/(?!\/)/g, 'src="https://kit19.com/')
                .replace(/href="public\//g, 'href="https://kit19.com/public/')
                .replace(/src="public\//g, 'src="https://kit19.com/public/');

            const parser = new DOMParser();
            const doc = parser.parseFromString(fixedHtmlString, 'text/html');
            
            // Extract links and styles from the head
            const headElements = Array.from(doc.head.querySelectorAll('link[rel="stylesheet"], style'));
            headElements.forEach(el => document.head.appendChild(el));

            const container = document.createElement('div');
            container.style.position = 'absolute';
            container.style.top = '-99999px';
            container.style.left = '-99999px';
            container.style.width = '1200px';
            container.style.background = '#fff';
            container.innerHTML = doc.body.innerHTML;
            document.body.appendChild(container);

            // Wait for styles, fonts, and images to load
            await new Promise(resolve => setTimeout(resolve, 2000));

            const opt = {
                margin:       [5, 5, 5, 5],
                filename:     `Invoice_${invoiceId}.pdf`,
                image:        { type: 'jpeg', quality: 0.98 },
                html2canvas:  { scale: 2, useCORS: true, logging: false, windowWidth: 1200 },
                jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
            };

            try {
                const pdfBlob = await html2pdf().from(container).set(opt).output('blob');
                
                if (!pdfBlob || pdfBlob.size === 0) {
                    throw new Error("Generated PDF is empty");
                }

                const downloadUrl = window.URL.createObjectURL(pdfBlob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = opt.filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(downloadUrl);
            } catch (err) {
                console.error("html2pdf generation error:", err);
                throw new Error("Failed to generate PDF document");
            } finally {
                // Cleanup
                document.body.removeChild(container);
                headElements.forEach(el => {
                    if (el.parentNode === document.head) {
                        document.head.removeChild(el);
                    }
                });
            }

            onClose();
        } catch (error) {
            console.error('Error downloading PDF:', error);
            Swal.fire({ title: 'Error', text: error.message || 'Failed to download PDF.', icon: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const filteredTemplates = templates.filter(t =>
        (t.templateName || t.TemplateName || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (!isOpen) return null;

    return (
        <div className="invoice-template-panel-overlay" onClick={onClose}>
            <div className="invoice-template-panel" onClick={e => e.stopPropagation()}>
                <div className="panel-header">
                    <div className="header-title">
                        Download Invoice PDF
                    </div>
                    <button className="close-btn" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="panel-body">
                    <div className="search-container">
                        <div className="search-input-wrapper">
                            <Search size={18} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search Template"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="template-list-container">
                        {loading && templates.length === 0 ? (
                            <div className="loader-container">
                                <Loader message="Fetching templates..." />
                            </div>
                        ) : filteredTemplates.length > 0 ? (
                            <div className="template-grid">
                                {filteredTemplates.map((temp, i) => (
                                    <div
                                        key={i}
                                        className={`template-item ${selectedTemplate?.templateId === temp.templateId ? 'selected' : ''}`}
                                        onClick={() => setSelectedTemplate(temp)}
                                    >
                                        <div className="radio-container">
                                            <div className={`radio-circle ${selectedTemplate?.templateId === temp.templateId ? 'checked' : ''}`}>
                                                {selectedTemplate?.templateId === temp.templateId && <div className="radio-inner" />}
                                            </div>
                                        </div>
                                        <div className="template-info">
                                            <div className="template-name">{temp.templateName || temp.TemplateName}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="no-templates">
                                {loading ? 'Fetching templates...' : 'No templates found matching your search.'}
                            </div>
                        )}
                    </div>
                </div>

                <div className="panel-footer">
                    <button className="footer-btn secondary" onClick={onClose}>Cancel</button>
                    <button
                        className="footer-btn primary"
                        disabled={!selectedTemplate || loading}
                        onClick={handleDownload}
                    >
                        {loading ? (
                            <>
                                <span className="btn-spinner"></span>
                                Processing...
                            </>
                        ) : (
                            "Download"
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InvoiceTemplatePanel;
